import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Prompt, withRouter } from 'react-router-dom';
import withStyles from 'react-jss';
import { Translation } from 'react-i18next';
import i18n from 'i18next';
import classNames from 'classnames';
import { isEmpty } from 'lodash';

import {
  getAuthState,
  getPreRegistrationState,
  getUserScore,
} from '../../store/selectors';
import { addScore, saveResults, getCurrentUserId } from '../../store/actions';
import ProgressBar from '../utility/ProgressBar';
import LoginButton from '../utility/LoginButton';
import Loading from '../utility/Loading';
import SchoolLogo from '../utility/SchoolLogo';
import { fireGTM } from '../../gtm/gtmUtil';
import { gamePage_GTM } from '../../gtm/datalayers/gameDataLayers.json';
import PoweredByASUBlackAndWhite from '../utility/PoweredByASUBlackAndWhite';
import WindowTitle from '../utility/WindowTitle';

/* eslint-disable react/jsx-props-no-spreading */

const NUM_PAIRS = 60;

const bars = {
  Onboarding: {
    current: 100,
    override: false,
  },
  'Picture Game': {
    current: 0, // override in component
    override: true,
  },
  'Career Path': {
    current: 0,
    override: false,
  },
};

const playButtonStyles = {
  color: (props) => ({
    backgroundColor: props.tenant.primary,
    borderColor: props.tenant.primary,

    '&:hover': {
      backgroundColor: 'white',
      border: `2px solid ${props.tenant.primary}`,
      color: props.tenant.primary,
      borderStyle: 'solid',
    },
  }),
};

class MainGame extends Component {
  _isMounted = false;

  constructor(props) {
    super(props);

    this.leftImageRef = React.createRef();

    this.state = {
      imagesLoaded: 0,
      progress: 0,
      results: {
        r: 0,
        i: 0,
        a: 0,
        s: 0,
        e: 0,
        c: 0,
      },
      showMilestoneText: false,
      retry: false,
    };
  }

  componentDidMount() {
    this._isMounted = true;

    const modeParam = new URLSearchParams(this.props.location.search);
    const retry = modeParam.get('retry');
    if (retry) {
      this.setState({
        retry: true,
      });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  get isAuthenticated() {
    return this.props.auth.isAuthenticated;
  }

  get jwToken() {
    return this.props.auth.jwtToken;
  }

  get milestoneText() {
    if (!this.state.showMilestoneText) return null;

    let text = i18n.t(`You're halfway there!`);

    if (this.percent >= 82)
      text = i18n.t('{{number}} more to go!', { number: '10' });

    if (this.percent >= 92)
      text = i18n.t('{{number}} more to go!', { number: '5' });

    return text;
  }

  get isPairLoaded() {
    return this.state.imagesLoaded >= 2;
  }

  getImage(side, progress) {
    try {
      return this.props.images[progress][side];
    } catch (err) {
      return null;
    }
  }

  get percent() {
    return Math.round((this.state.progress * 100) / NUM_PAIRS);
  }

  secondaryBorder = {
    borderBottom: `5px solid ${this.props.tenant.secondary}`,
  };

  largeSecondaryBorder = {
    borderTop: `11px solid ${this.props.tenant.secondary}`,
  };

  triggerMilestone() {
    if (!this._isMounted) return;

    this.setState({ showMilestoneText: true });

    setTimeout(() => {
      if (this._isMounted) {
        this.setState({ showMilestoneText: false });
      }
    }, 5000);
  }

  completeGame = async () => {
    await this.props.addScore(this.state.results);

    let userId;
    if (this.isAuthenticated)
      userId = await this.props.getCurrentUserId(this.props.auth.jwtToken);

    if (
      this.state.retry ||
      (this.isAuthenticated && !isEmpty(this.props.userScore) && userId)
    ) {
      const score = {
        riasecValues: this.props.userScore,
      };
      await this.props.saveResults(score, this.jwToken);
      await this.props.history.push({
        pathname: 'career-path/top-3',
      });
    } else {
      await this.props.history.push({
        pathname: 'registration',
      });
    }
  };

  onPictureSelect = async (selectedPicture) => {
    this.leftImageRef.current.focus();
    if (!this.isPairLoaded) return;

    gamePage_GTM.imageSelected.text = selectedPicture.url;
    fireGTM(gamePage_GTM.imageSelected);

    this.setState(
      (prevState) => ({
        imagesLoaded: 0,
        progress: prevState.progress + 1,
        results: {
          ...prevState.results,
          [selectedPicture.riasecCharacter]:
            prevState.results[selectedPicture.riasecCharacter] + 1,
        },
      }),
      () => {
        if (this.state.progress === NUM_PAIRS) this.completeGame();

        if ([50, 82, 92].includes(this.percent)) this.triggerMilestone();
      }
    );
  };

  onPictureLoadSuccess = () => {
    this.setState(({ imagesLoaded }) => ({ imagesLoaded: imagesLoaded + 1 }));
  };

  onPictureLoadError = (ev) => {
    console.log('Error loading image', ev.target.getAttribute('src'));
  };

  renderImage(side) {
    const imageNum = side === 'left' ? 1 : 2;
    const image = this.getImage(side, this.state.progress);
    const previousImage = this.getImage(side, this.state.progress - 1);
    const visibleImage = this.isPairLoaded ? image : previousImage;
    const visibleImageProps = this.isPairLoaded
      ? { [`data-test-game-image-${imageNum}`]: true }
      : {};

    return (
      <div className="main-game__image-container">
        {/* Loading Image */}
        {this.isPairLoaded ? null : (
          <img
            aria-hidden
            className={classNames({
              'main-game__image-container-image': true,
              'main-game__image-container-image--hidden': true,
            })}
            onLoad={this.onPictureLoadSuccess}
            onError={this.onPictureLoadError}
            src={image.url}
            alt={image.description}
          />
        )}
        {/* Visible Image */}
        {visibleImage ? (
          <button
            ref={side === 'left' ? this.leftImageRef : null}
            alt={visibleImage.description}
            className={classNames({
              'main-game__image-container-image': true,
              'main-game__image-container-image--loading': !this.isPairLoaded,
              'focus-indicator': true,
            })}
            id={`pic${imageNum}`}
            onClick={() => this.onPictureSelect(visibleImage)}
            onKeyDown={(e) => {
              if (['Enter'].includes(e.key)) this.onPictureSelect(visibleImage);
            }}
            type="button"
            tabIndex={0}
            aria-label={`click to select image of ${visibleImage.description}`}
            style={{
              backgroundImage: `url(${visibleImage.url})`,
              backgroundSize: 'cover',
              borderColor: 'white',
              borderWidth: '0.01px', // If we do a 0px border, or border: none, _some_ images on android won't load sometimes. Only when they're served from cache though. ¯\_(ツ)_/¯ video: https://clip.brianschiller.com/rxr1K2n-2021-07-06-simplescreenrecorder-2021-07-06_10.55.20.mkv
            }}
            title={visibleImage.description}
            {...visibleImageProps}
          />
        ) : null}
      </div>
    );
  }

  render() {
    if (this.state.progress === NUM_PAIRS) {
      return <Loading height="100vh" />;
    }

    if (this.props.images.length === 0) {
      return <Loading height="100vh" />;
    }

    return (
      <Translation>
        {(t) => (
          <>
            <WindowTitle>{t('Picture Game')}</WindowTitle>
            <div id="main-game" style={this.largeSecondaryBorder}>
              <Prompt
                message={t(
                  'Warning! Your responses will be lost. Do you want to continue?'
                )}
              />
              <div className="top-section">
                <LoginButton
                  tenant={this.props.tenant}
                  color={this.props.tenant.primary}
                  isAuthenticated={this.isAuthenticated}
                  section="image selection"
                />
                <div className="logo-text-container">
                  <SchoolLogo
                    schoolLogoWidth="100%"
                    horizontalAlign="flex-start"
                    containerMargin="20px 0 20px 0"
                    schoolLogoContainerMinWidth="0"
                  />
                  <div className="header-text-container">
                    <h1>{t(`Let's build your career profile!`)}</h1>
                    <p className="pick-one-text">{t('Please Pick One:')}</p>
                    <div className="fake-border" style={this.secondaryBorder} />
                  </div>
                  {/* Need this to center the header text */}
                  <div className="column is-one-quarter asu-logo-container-game-dummy" />
                </div>
              </div>
              <div className="picture-container">
                <>
                  {this.renderImage('left')}
                  <div className="divider-container">
                    <div className="divider" />
                    <p className="or">
                      {t('or', { context: 'conjunction' }).toUpperCase()}
                    </p>
                    <div className="divider" />
                  </div>
                  {this.renderImage('right')}
                </>
              </div>
              <div className="progress-component">
                <ProgressBar
                  bars={bars}
                  currentPhase={this.percent}
                  milestone
                  milestoneText={this.milestoneText}
                  tenant={this.props.tenant}
                />
              </div>
              <PoweredByASUBlackAndWhite tenant={this.props.tenant} />
            </div>
          </>
        )}
      </Translation>
    );
  }
}

const mapStateToProps = (state) => {
  const preregistration = getPreRegistrationState(state);
  const images = preregistration.imagePairs;

  const auth = getAuthState(state);
  const tenant = state.tenant;
  const userScore = getUserScore(state);

  return { auth, preregistration, images, tenant, userScore };
};

const GameWithRouter = withRouter(
  connect(mapStateToProps, {
    addScore,
    saveResults,
    getCurrentUserId,
  })(MainGame)
);

export default withStyles(playButtonStyles)(GameWithRouter);
